import Joi from 'joi';

import { assert } from '../utils/assert';
import {
    descriptionLanguagesSchema,
    displayNameLanguagesSchema,
} from '../utils/utils';

import { PackType } from '../types/enums/packtype.enum';

const {
    PACK,
    STANDALONE,
    MULTIPACK,
    PLAYSET,
    HOME_DESIGNER,
    OUTFIT_MAKER,
    CHARACTER_CREATOR,
} = PackType;

// Shared display name and features schemas
export const displayName = Joi.object()
    .keys(displayNameLanguagesSchema)
    .required()
    .unknown(true);

export const features = Joi.object()
    .keys(descriptionLanguagesSchema)
    .unknown(true);

// Shared pack type schema factory
export const packTypeSchema = (itemType: string) =>
    Joi.string()
        .valid(
            MULTIPACK,
            PLAYSET,
            HOME_DESIGNER,
            OUTFIT_MAKER,
            CHARACTER_CREATOR,
        )
        .invalid(STANDALONE, PACK)
        .messages({
            'any.invalid': `Packs of the type "${itemType}" are deprecated and changes are restricted`,
        })
        .required();

// Shared scenes warning schema
export const scenesWarningSchema = Joi.array()
    .when('type', {
        is: Joi.alternatives().try(
            Joi.string().valid(MULTIPACK),
            Joi.string().valid(PACK),
        ),
        then: Joi.array().length(0).required(),
    })
    .when('type', {
        is: PLAYSET,
        then: Joi.array().min(1).items(Joi.string()).required(),
        otherwise: Joi.array().min(0).items(Joi.string()).required(),
    })
    .messages({
        'array.min': `"${PLAYSET}" pack should have at least 1 scene`,
        'array.length': `"${MULTIPACK}" type should have no scenes`,
        'any.required': 'Scene count is not correct for this type',
    });

// Shared content warning schema
export const contentWarningSchema = Joi.array()
    .when('type', {
        is: Joi.alternatives().try(
            Joi.string().valid(MULTIPACK),
            Joi.string().valid(PACK),
        ),
        then: Joi.array().length(0).required(),
        otherwise: Joi.array().min(1).items(Joi.string()).required(),
    })
    .messages({
        'array.length': `"${MULTIPACK}" type should not have content id`,
        'array.min': 'Pack must have at least 1 content id',
        'any.required': 'Content count is incorrect for this pack type',
    });

// Shared child packs warning schema factory
export const childPacksWarningSchema = <T extends { packId: string }>(
    requiredVersion: number,
    childPacks: T[],
) =>
    Joi.array()
        .when('type', {
            is: Joi.alternatives().try(
                Joi.string().valid(MULTIPACK),
                Joi.string().valid(PACK),
            ),
            then: Joi.array()
                .min(1)
                .items(
                    Joi.object()
                        .keys({
                            name: Joi.string(),
                            requiredVersion: Joi.number()
                                .integer()
                                .custom((value, helpers) => {
                                    if (
                                        value > requiredVersion &&
                                        Array.isArray(helpers.state.path)
                                    ) {
                                        const path = helpers.state.path[1];
                                        assert(typeof path === 'number');
                                        return helpers.error(
                                            'childPack.requiredVersion',
                                            {
                                                limit: requiredVersion,
                                                packName:
                                                    childPacks[path].packId,
                                            },
                                        );
                                    }
                                    return value;
                                })
                                .messages({
                                    'childPack.requiredVersion': `Child pack '{#packName}' has a higher required version ({#value}) than this pack ({#limit})`,
                                }),
                        })
                        .unknown(true),
                )
                .required(),
            otherwise: Joi.array().length(0),
        })
        .messages({
            'array.min': `${MULTIPACK} should have at least 1 pack in its content`,
            'array.sparse': 'Some of the content packs are missed',
            'array.length':
                'Nr of districts and playsets are not correct for this pack type',
        });

// Shared active date error schemas
export const activeFromDateErrorSchema = Joi.date()
    .required()
    .less(Joi.ref('activeToDateCopy'))
    .messages({
        'date.base': '"Starts on" date must be selected',
        'date.less': '"Starts on" date must be before "Ends on" date',
    });

export const activeToDateErrorSchema = Joi.date()
    .required()
    .greater(Joi.ref('activeFromDate'))
    .messages({
        'date.base': '"Ends on" date must be selected',
        'date.greater': '"Ends on" date must be after "Starts on" date',
    });

// Shared helper to compute bundle worth coverage
export const computeIsBundleWorthCovered = <
    TChild extends { worth: number },
    TPriceLevel extends { min: number; max: number },
>(
    childPacks: TChild[],
    priceLevels: TPriceLevel[],
): boolean => {
    const totalWorth = childPacks.reduce((acc, pack) => acc + pack?.worth, 0);
    const totalWorthInPriceId = priceLevels.find(
        (priceLevel) =>
            priceLevel.min <= totalWorth && totalWorth <= priceLevel.max,
    );
    return !!totalWorthInPriceId;
};

// Shared warning field definitions
export const sharedWarningFields = {
    packId: Joi.string().required(),
    name: Joi.string().required(),
    requiredVersion: Joi.number().required(),
    worth: Joi.number().integer().min(0),
    priority: Joi.number().integer().min(0),
    activeFromDate: Joi.date().required(),
    activeToDate: Joi.date().required(),
    activeToDateCopy: Joi.any(),
    outfits: Joi.number().integer(),
    accessories: Joi.number().integer(),
    hairStyles: Joi.number().integer(),
    tags: Joi.array().items(Joi.number().integer()),
    carouselIds: Joi.array(),
};

// Shared error field definitions (numeric counts)
export const sharedErrorNumericFields = {
    locations: Joi.number().integer().required(),
    characters: Joi.number().integer().required(),
    templates: Joi.number().integer().required(),
    furniture: Joi.number().integer().required(),
    surfaceDesigns: Joi.number().integer().required(),
    garments: Joi.number().integer().required(),
    patterns: Joi.number().integer().required(),
    stickers: Joi.number().integer().required(),
};
